import ParameterCalculator from "@/components/ParameterCalculator";
import { QUALITATIVE_RUBRICS } from "@/lib/qualitativeRubric";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, ChevronRight, ChevronLeft, Loader2, CheckCircle2, Calculator, Upload, Download, Zap, Info } from "lucide-react";
import { API_BASE_URL, BACKEND_BASE_URL } from "../../config/api";
import MaturityDropdown from "@/components/MaturityDropdown";
import { isQualitative } from "@/lib/qualitativeRubric";
import { TOSS_LOGO_URL as LOGO_URL, BRAND } from "@/lib/branding";

const API = API_BASE_URL;
const BASIC = `${BACKEND_BASE_URL}/api/basic`;

const TRANSPARENCY_OPTIONS = [
  { value: "2", label: "Estimated" },
  { value: "5", label: "Self-reported" },
  { value: "10", label: "Third-party verified" },
];

const STAGE_TABS = [
  { key: "manufacturing", label: "Manufacturing" },
  { key: "use_phase", label: "Use Phase" },
  { key: "logistics", label: "Logistics" },
];

const bomMassKg = (items = []) => items.reduce(
  (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.weight_kg) || 0),
  0
);

export default function BasicNewAssessment() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const [draftId] = useState(() => query.get('draft') || crypto.randomUUID());
  const [ready, setReady] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [benchmarks, setBenchmarks] = useState({});
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState('');
  const [revisionOf, setRevisionOf] = useState(null);
  const savingQueue = useRef(Promise.resolve());
  const completed = useRef(false);
  const [step, setStep] = useState(1);
  const [meta, setMeta] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [countries, setCountries] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const bomFileRef = useRef(null);
  const stageFileRef = useRef(null);

  const [form, setForm] = useState({
    product_name: "",
    functional_unit: "1 Kg Product",
    product_mass: 0,
    product_type: "",
    country: "India",
    transparency: 5,
    bom_items: [{ material: "", country: "India", component: "", quantity: 1, weight_kg: 0 }],
  });
  const [stageInputs, setStageInputs] = useState({});
  const [stageInputUnits, setStageInputUnits] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [metaRes, matRes, cRes, ptRes] = await Promise.all([
          axios.get(`${BASIC}/meta`),
          axios.get(`${BASIC}/materials`),
          axios.get(`${BASIC}/countries`),
          axios.get(`${BASIC}/product-types`),
        ]);
        setMeta(metaRes.data);
        setMaterials(matRes.data);
        setCountries(cRes.data);
        setProductTypes(ptRes.data);

        const init = {};
        const initUnits = {};
        for (const s of STAGE_TABS) {
          init[s.key] = {};
          initUnits[s.key] = {};
          for (const pk of metaRes.data.param_keys) {
            init[s.key][pk] = null;
            initUnits[s.key][pk] = metaRes.data.param_units[pk];
          }
        }
        setStageInputs(init);
        setStageInputUnits(initUnits);
        if (query.get('draft')) {
          const {data} = await axios.get(`${BASIC}/drafts/${draftId}`);
          const p = data.payload;
          setForm(p.form); setStageInputs(p.stageInputs || {}); setStageInputUnits(p.stageInputUnits || initUnits); setStep(p.step || 1); setRevisionOf(p.revisionOf || null);
        } else if (query.get('copy') || query.get('revise')) {
          const {data} = await axios.get(`${BASIC}/assessments/${query.get('copy') || query.get('revise')}`);
          setForm({...data, product_name: query.get('copy') ? `${data.product_name} (copy)` : data.product_name});
          setStageInputs(data.stage_inputs || {}); setStageInputUnits(data.stage_input_units || initUnits);
          setRevisionOf(query.get('revise') || null);
        }
        setReady(true);
      } catch (e) {
        toast.error("Failed to load data. Please seed Basic data first from the Dashboard.");
      }
    })();
  }, [draftId, query]);

  const saveDraft = useCallback(() => {
    if (!ready || completed.current) return Promise.resolve();
    setSaveStatus('Saving…');
    const payload = {form, stageInputs, stageInputUnits, step, revisionOf};
    savingQueue.current = savingQueue.current.catch(() => {}).then(() => axios.put(`${BASIC}/drafts/${draftId}`, {payload})).then(() => setSaveStatus('Saved')).catch(() => setSaveStatus('Not saved — retry Save draft'));
    return savingQueue.current;
  }, [ready, form, stageInputs, stageInputUnits, step, revisionOf, draftId]);
  useEffect(() => { if (!ready) return; const timer = setTimeout(saveDraft, 650); return () => clearTimeout(timer); }, [saveDraft, ready]);
  useEffect(() => {
    if (!form.product_type) return;
    let active = true;
    const load = () => axios.get(`${BASIC}/product-benchmarks/${form.product_type}`).then(r => {if (active) setBenchmarks(r.data);});
    load().catch(() => setIssues([{message:'Could not load benchmark availability. Please retry.'}]));
    window.addEventListener('focus', load);
    return () => {active=false; window.removeEventListener('focus', load);};
  }, [form.product_type]);
  const available = (stage, pk) => benchmarks[stage]?.input_parameters?.[pk]?.status === 'applicable' || (!benchmarks[stage] && isQualitative(pk));

  const updateBOMItem = useCallback((index, field, value) => {
    setForm(prev => {
      const items = [...prev.bom_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, bom_items: items };
    });
  }, []);

  const addBOMItem = () => setForm(p => ({ ...p, bom_items: [...p.bom_items, { material: "", country: p.country || "India", component: "", quantity: 1, weight_kg: 0 }] }));
  const removeBOMItem = (idx) => setForm(p => ({ ...p, bom_items: p.bom_items.filter((_, i) => i !== idx) }));
  const updateParam = (stage, param, value) => setStageInputs(prev => ({ ...prev, [stage]: { ...prev[stage], [param]: value === "" || value == null ? null : Number(value) } }));

  const handleBOMImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await axios.post(`${BASIC}/import-assessment-excel`, fd);
      if (res.data.errors?.length) {toast.error(res.data.errors[0]); return;}
      if (res.data.bom_items?.length > 0) {
        setForm(p => ({ ...p, bom_items: res.data.bom_items }));
        toast.success(`Imported ${res.data.bom_items.length} BOM items`);
      } else {
        toast.error("No BOM data found. Ensure sheet 'BOM' exists.");
      }
    } catch { toast.error("Import failed"); }
    if (bomFileRef.current) bomFileRef.current.value = "";
  };

  const handleStageImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await axios.post(`${BASIC}/import-assessment-excel`, fd);
      const unitErrors = res.data.errors || [];
      if (unitErrors.length > 0) {
        toast.error(`${unitErrors[0]}. Download the updated Stage Data template and include units.`);
        if (stageFileRef.current) stageFileRef.current.value = "";
        return;
      }
      const si = res.data.stage_inputs || {};
      if (Object.keys(si).length > 0) {
        setStageInputs(prev => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(si)) merged[k] = { ...(merged[k] || {}), ...v };
          return merged;
        });
        if (Object.keys(res.data.stage_input_units || {}).length > 0) {
          setStageInputUnits(prev => {
            const merged = { ...prev };
            for (const [k, v] of Object.entries(res.data.stage_input_units)) {
              merged[k] = { ...(merged[k] || {}), ...v };
            }
            return merged;
          });
        }
        toast.success("Stage data imported");
      } else { toast.error("No stage data found"); }
    } catch { toast.error("Import failed"); }
    if (stageFileRef.current) stageFileRef.current.value = "";
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setIssues([]);
    await saveDraft();
    try {
      const totalWeight = bomMassKg(form.bom_items);
      const payload = {
        draft_id: draftId, revision_of: revisionOf,
        product_name: form.product_name,
        functional_unit: form.functional_unit,
        product_type: form.product_type,
        product_mass: totalWeight,
        country: form.country,
        transparency: parseInt(form.transparency) || 5,
        bom_items: form.bom_items.map(b => ({ ...b, country: b.country || form.country })),
        stage_inputs: stageInputs,
        stage_input_units: stageInputUnits,
      };
      const res = await axios.post(`${BASIC}/assessments`, payload);
      completed.current = true;
      toast.success(`Basic TOSS Index: ${(res.data.results.upsi * 10).toFixed(2)}/100`);
      navigate(`/basic/assessments/${res.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setIssues(Array.isArray(detail?.issues) ? detail.issues : [{message: typeof detail === "string" ? detail : detail?.message || "Calculation failed"}]);
      toast.error(typeof detail === "string" ? detail : detail?.message || "Calculation failed");
    } finally { setSubmitting(false); }
  };

  if (!meta) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  const canProceedStep1 = form.product_name && form.product_type && form.country && form.bom_items.every(i => i.material && i.weight_kg > 0 && i.quantity > 0);
  const wizardSteps = ["BOM & Setup", "Stage Inputs", "Review & Calculate"];

  return (
    <div className="p-4 sm:p-8 max-w-7xl" data-testid="basic-new-assessment">
      <div className="flex items-center gap-3 mb-2">
        <img src={LOGO_URL} alt={BRAND.short} className="h-8 w-auto object-contain" />
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>New Basic Assessment</h1>
        <Badge className="bg-[#43A047] text-white text-[10px] tracking-wider"><Zap className="w-3 h-3 mr-1" />BASIC MODE</Badge>
      </div>
      <p className="text-sm text-[#617364] mb-6">Simplified 5-stage {BRAND.short} — Raw Material &amp; EOL auto-derived from BOM. You only fill Manufacturing, Use Phase &amp; Logistics.</p>

      <div className="flex flex-wrap items-center gap-4 mb-6"><span role="status" className="text-sm text-green-800">{saveStatus}</span><Button variant="outline" onClick={saveDraft}>Save draft</Button><Button variant="ghost" onClick={async () => {await saveDraft(); navigate('/basic/assessments');}}>All assessments &amp; drafts</Button></div>
      <input type="file" ref={bomFileRef} accept=".xlsx,.xls" onChange={handleBOMImport} className="hidden" />
      <input type="file" ref={stageFileRef} accept=".xlsx,.xls" onChange={handleStageImport} className="hidden" />

      <div className="wizard-step-indicator mb-8 border-b border-[#E0E6E1]">
        {wizardSteps.map((label, i) => (
          <button key={i} className={`wizard-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "completed" : ""}`}
            onClick={() => { if (i + 1 < step) setStep(i + 1); }} data-testid={`basic-wizard-${i + 1}`}>
            <span className="step-num">{step > i + 1 ? <CheckCircle2 className="w-3 h-3" /> : i + 1}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-6" data-testid="basic-step-1">
          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Product Controls</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(`${BASIC}/template/bom`, '_blank')} className="border-[#E0E6E1] text-[#617364]"><Download className="w-3.5 h-3.5 mr-1" /> Template</Button>
                  <Button variant="outline" size="sm" onClick={() => bomFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]"><Upload className="w-3.5 h-3.5 mr-1" /> Import Excel</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Name</Label>
                  <Input value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))} placeholder="e.g. 500 mL Glass Bottle" className="mt-1 border-[#E0E6E1]" data-testid="basic-product-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Type</Label>
                  <Select value={form.product_type} onValueChange={v => setForm(p => ({ ...p, product_type: v }))}>
                    <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="basic-product-type"><SelectValue placeholder="Select product type" /></SelectTrigger>
                    <SelectContent>{productTypes.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Selected Country</Label>
                  <Select value={form.country} onValueChange={v => setForm(p => ({ ...p, country: v, bom_items: p.bom_items.map(b => ({ ...b, country: v })) }))}>
                    <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="basic-country"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Data Transparency (Global)</Label>
                  <Select value={String(form.transparency)} onValueChange={v => setForm(p => ({ ...p, transparency: parseInt(v) }))}>
                    <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="basic-transparency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRANSPARENCY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.value} — {o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#617364] mt-1">Applies to all parameter scores. Final Score = Raw × (T / 10).</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Reporting functional unit</Label>
                  <Select value={form.functional_unit} onValueChange={v => setForm(p => ({...p, functional_unit:v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1 Kg Product">Per 1 kg of product</SelectItem><SelectItem value="1 Unit">Per 1 product</SelectItem></SelectContent></Select><p className="text-xs text-slate-500 mt-2">Controls physical quantities in the report. Each input retains the unit shown in its row.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Bill of Materials</CardTitle>
                <Button onClick={addBOMItem} variant="outline" size="sm" className="border-[#E0E6E1] text-[#43A047]" data-testid="basic-add-bom"><Plus className="w-4 h-4 mr-1" /> Add Material</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow className="border-[#E0E6E1]">
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Material</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Component</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Qty</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Unit Weight (Kg)</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Mass Share</TableHead>
                  <TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {form.bom_items.map((item, idx) => {
                    const totalW = bomMassKg(form.bom_items) || 1;
                    const lineMass = (Number(item.quantity) || 0) * (Number(item.weight_kg) || 0);
                    const mf = (lineMass / totalW * 100).toFixed(2);
                    return (
                      <TableRow key={idx} className="border-[#E0E6E1]">
                        <TableCell>
                          <Select value={item.material} onValueChange={v => updateBOMItem(idx, "material", v)}>
                            <SelectTrigger className="border-[#E0E6E1] w-[150px]" data-testid={`basic-bom-mat-${idx}`}><SelectValue placeholder="Material" /></SelectTrigger>
                            <SelectContent>{materials.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell><Input value={item.component} onChange={e => updateBOMItem(idx, "component", e.target.value)} placeholder="Component" className="border-[#E0E6E1] w-[160px]" /></TableCell>
                        <TableCell><Input type="number" min="1" value={item.quantity} onChange={e => updateBOMItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="border-[#E0E6E1] w-[70px]" /></TableCell>
                        <TableCell><Input type="number" min="0" step="0.001" value={item.weight_kg} onChange={e => updateBOMItem(idx, "weight_kg", parseFloat(e.target.value) || 0)} className="border-[#E0E6E1] w-[100px]" data-testid={`basic-bom-wt-${idx}`} /></TableCell>
                        <TableCell><span className="text-xs text-[#617364]">{mf}%</span></TableCell>
                        <TableCell>{form.bom_items.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeBOMItem(idx)} className="text-[#D32F2F]"><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-3 text-sm text-[#617364]">Total Product Mass (Σ quantity × unit weight): <span className="font-medium text-[#1B291D]">{bomMassKg(form.bom_items).toFixed(4)} Kg</span></div>
              <div className="mt-3 p-3 rounded-lg bg-[#43A047]/5 border border-[#43A047]/20">
                <p className="text-xs text-[#388E3C]"><strong>Auto-calculated:</strong> Raw Material &amp; End-of-Life are derived from BOM composition and material benchmarks — no manual input required.</p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="basic-next-1">Next: Stage Inputs <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="basic-step-2">
          <div className="bg-white border border-[#E0E6E1] rounded-lg p-4 mb-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#617364]">Enter values for <strong className="text-[#1B291D]">Manufacturing, Use Phase &amp; Logistics</strong> only. Each stage = single input block (no substages). Raw Material &amp; EOL are auto-computed.</p>
              <div className="flex gap-2 ml-4 shrink-0">
                <Button variant="outline" size="sm" onClick={() => window.open(`${BASIC}/template/stage-data`, '_blank')} className="border-[#E0E6E1] text-[#617364]"><Download className="w-3.5 h-3.5 mr-1" /> Template</Button>
                <Button variant="outline" size="sm" onClick={() => stageFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]"><Upload className="w-3.5 h-3.5 mr-1" /> Import</Button>
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-600">Inputs use the units shown below; new quantity entries are per product. Missing product benchmarks or N/A exclude the parameter. Blank applicable inputs receive an assumed 0/100 score, never zero emissions. Rubrics remain unless marked N/A.</p>
          <Tabs defaultValue="manufacturing" className="w-full">
            <TabsList className="bg-[#F8F9F8] border border-[#E0E6E1] p-1 mb-4">
              {STAGE_TABS.map(st => (
                <TabsTrigger key={st.key} value={st.key} className="data-[state=active]:bg-white data-[state=active]:text-[#43A047] text-sm" data-testid={`basic-tab-${st.key}`}>{st.label}</TabsTrigger>
              ))}
            </TabsList>
            {STAGE_TABS.map(st => (
              <TabsContent key={st.key} value={st.key} className="space-y-6">
                <Card className="border-[#E0E6E1] bg-white">
                  <CardHeader className="pb-3"><CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{st.label} — {meta.param_keys.filter(pk => available(st.key,pk)).length} applicable parameters</CardTitle></CardHeader>
                  <CardContent>
                    <Input aria-label="Search parameters" placeholder="Search parameters…" value={search} onChange={e => setSearch(e.target.value)} className="mb-4 max-w-sm"/>
                    <Table>
                      <TableHeader><TableRow className="border-[#E0E6E1]">
                        <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold w-[240px]">Parameter</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">Value ({st.label})</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold w-[120px]">Input unit</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {meta.param_keys.filter(pk => available(st.key,pk) && meta.param_labels[pk].toLowerCase().includes(search.toLowerCase())).map(pk => {
                          const qual = isQualitative(pk);
                          return (
                          <TableRow key={pk} className="border-[#E0E6E1]">
                            <TableCell className="py-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium text-[#1B291D]">{meta.param_labels[pk]}</span><ParameterCalculator parameter={pk}/>
                                {qual ? (
                                  <Badge variant="outline" className="text-[8px] px-1 py-0 border-[#43A047] text-[#388E3C]">rubric</Badge>
                                ) : meta.param_types[pk] === "direct" ? <Badge variant="outline" className="text-[8px] px-1 py-0 border-[#81C784] text-[#43A047]">0-10</Badge> : null}
                                {meta.param_types[pk] === "lower_is_better" && !qual && <Badge variant="outline" className="text-[8px] px-1 py-0 border-[#FFCC80] text-[#F57F17]">lower↓</Badge>}
                                {meta.param_types[pk] === "higher_is_better" && !qual && <Badge variant="outline" className="text-[8px] px-1 py-0 border-[#81C784] text-[#43A047]">higher↑</Badge>}
                                {qual && <Info className="w-3 h-3 text-[#617364]" title="Maturity-based qualitative scoring" />}
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5">
                              {qual ? (
                                <Select value={stageInputs[st.key]?.[pk] == null ? '' : String(stageInputs[st.key][pk])} onValueChange={v => updateParam(st.key,pk,v === 'clear' ? null : v)}><SelectTrigger data-testid={`basic-in-${st.key}-${pk}`} className="min-w-0 h-auto min-h-10 text-left whitespace-normal"><SelectValue placeholder="Select rubric…"/></SelectTrigger><SelectContent><SelectItem value="clear">Clear selection</SelectItem>{QUALITATIVE_RUBRICS[pk].options.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}{stageInputs[st.key]?.[pk] != null && ![2,5,10].includes(Number(stageInputs[st.key][pk])) && <SelectItem value={String(stageInputs[st.key][pk])}>Existing score: {stageInputs[st.key][pk]}/10</SelectItem>}</SelectContent></Select>
                              ) : (
                                <Input type="number" step="any" value={stageInputs[st.key]?.[pk] ?? ""} onChange={e => updateParam(st.key, pk, e.target.value)} placeholder="Enter value" className="border-[#E0E6E1] text-sm h-10" data-testid={`basic-in-${st.key}-${pk}`} />
                              )}
                            </TableCell>
                            <TableCell className="py-1.5 text-xs text-[#617364]">{qual ? "Score 0–10" : (stageInputUnits[st.key]?.[pk] || meta.param_units[pk])}</TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#E0E6E1]" data-testid="basic-prev-2"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={() => setStep(3)} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="basic-next-2">Next: Review &amp; Calculate <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="basic-step-3"><p className="p-4 rounded-xl bg-green-50 text-green-950">Process B combines Parameter Avg. and ESGE at stage level, then combines the stage scores and ESGE shortfall component for the final TOSS Index.</p>{issues.length > 0 && <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-xl"><b>Check these inputs</b>{issues.map((i,n)=><p key={n}>{i.location}: {i.message}</p>)}</div>}
          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader><CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Review Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product</p><p className="text-sm text-[#1B291D] font-medium">{form.product_name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Type</p><p className="text-sm text-[#1B291D] font-medium">{productTypes.find(pt => pt.id === form.product_type)?.name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Country</p><p className="text-sm text-[#1B291D] font-medium">{form.country}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Transparency</p><p className="text-sm text-[#1B291D] font-medium">{form.transparency} — {TRANSPARENCY_OPTIONS.find(o => parseInt(o.value) === form.transparency)?.label}</p></div>
              </div>
              <div className="border-t border-[#E0E6E1] pt-4">
                <p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold mb-2">BOM ({form.bom_items.length} components, {bomMassKg(form.bom_items).toFixed(4)} Kg)</p>
                {form.bom_items.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 text-sm text-[#1B291D] py-1">
                    <Badge variant="outline" className="border-[#81C784] text-[#43A047]">{item.material}</Badge>
                    <span>{item.component || "—"}</span>
                    <span className="text-[#617364]">{item.quantity ?? 1} × {item.weight_kg} Kg = {((Number(item.quantity) || 0) * (Number(item.weight_kg) || 0)).toFixed(4)} Kg</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E0E6E1] pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
                  <p className="text-sm font-medium text-[#43A047]">Auto-calculated Stages</p>
                  <p className="text-xs text-[#617364] mt-1">Raw Material &amp; End-of-Life — composition-weighted from BOM + material benchmarks (country vs. world).</p>
                </div>
                <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
                  <p className="text-sm font-medium text-[#43A047]">User-input Stages</p>
                  <p className="text-xs text-[#617364] mt-1">Manufacturing, Use Phase, Logistics — scored against best/worst product benchmarks.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="border-[#E0E6E1]" data-testid="basic-prev-3"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#43A047] hover:bg-[#388E3C] text-white px-8" data-testid="basic-calculate">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Calculating...</> : <><Calculator className="w-4 h-4 mr-2" /> Calculate TOSS Index</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
